import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import LocationChangeModal from './LocationChangeModal'
import LocationSuccessModal from './LocationSuccessModal'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../hooks/useAuth'
import { resolveImageUrl, getUserPlaceholderImage } from '../../utils/imageUtils'

const EditProfileModal = ({ isOpen, onClose, userData, onSave }) => {
  const { t, i18n } = useTranslation()
  const { getUserType } = useAuth()

  // Get user type color
  const getUserTypeColor = () => {
    const userType = getUserType()
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
      'FIRM': '#1B3A6B', // Navy Blue
    }
    return userTypeConfig[userType] || '#8548A8' // Default to purple
  }

  // Helper function to darken color for hover
  const darkenColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16)
    const amt = Math.round(2.55 * percent)
    const R = Math.max(0, Math.min(255, (num >> 16) + amt))
    const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt))
    const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt))
    return "#" + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)
  }

  const userTypeColor = getUserTypeColor()
  const hoverColor = darkenColor(userTypeColor, -15)
  const [editedData, setEditedData] = useState(userData)

  // Reset editedData when userData changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setEditedData(userData)
    }
  }, [isOpen, userData])

  const [isLoading, setIsLoading] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [selectedCountryCode, setSelectedCountryCode] = useState('+966')
  const [isCountryCodeOpen, setIsCountryCodeOpen] = useState(false)
  const [countrySearchTerm, setCountrySearchTerm] = useState('')

  const [countries, setCountries] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const isRTL = i18n.dir() === 'rtl';

  const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/locations/countries-only`);
        if (response.ok) {
          const result = await response.json();
          if (result.status === 'success' && Array.isArray(result.data)) {
            setCountries(result.data);
            const options = result.data.map(country => ({
              value: country.id,
              label: isRTL ? (country.name_ar || country.name_en) : country.name_en
            }));
            setCountryOptions(options);
          }
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    fetchCountries();
  }, [isRTL]);

  // Update country name if we have ID but no name (or name is ID)
  useEffect(() => {
    if (countries.length > 0 && editedData.countryId && (!editedData.country || !isNaN(editedData.country))) {
      const country = countries.find(c => c.id === editedData.countryId);
      if (country) {
        const countryName = isRTL ? (country.name_ar || country.name_en) : country.name_en;
        setEditedData(prev => ({ ...prev, country: countryName }));
      }
    }
  }, [countries, editedData.countryId, isRTL]);

  // Update country ID if we have Name but no ID, OR if Country IS the ID
  useEffect(() => {
    // Case 1: Country is a number (ID)
    if (countries.length > 0 && !editedData.countryId && editedData.country && !isNaN(editedData.country)) {
      const countryId = Number(editedData.country);
      const country = countries.find(c => c.id === countryId);
      if (country) {
        setEditedData(prev => ({ ...prev, countryId: countryId }));
      }
    }
    // Case 2: Country is a string (Name) -> Find ID
    else if (countries.length > 0 && !editedData.countryId && editedData.country && isNaN(editedData.country)) {
      const countryNameStr = String(editedData.country).toLowerCase();
      const country = countries.find(c =>
        (c.name_en && String(c.name_en).toLowerCase() === countryNameStr) ||
        (c.name_ar && c.name_ar === editedData.country)
      );
      if (country) {
        setEditedData(prev => ({ ...prev, countryId: country.id }));
      }
    }
  }, [countries, editedData.country, editedData.countryId]);

  // Fetch cities when country changes
  useEffect(() => {
    const fetchCities = async () => {
      if (!editedData.countryId) {
        setCities([]);
        setCityOptions([]);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/locations/cities/country/${editedData.countryId}`);
        if (response.ok) {
          const result = await response.json();
          if (result.status === 'success' && Array.isArray(result.data)) {
            setCities(result.data);
            const options = result.data.map(city => ({
              value: city.id,
              label: isRTL ? (city.name_ar || city.name_en) : city.name_en
            }));
            setCityOptions(options);
          }
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
        setCities([]);
        setCityOptions([]);
      }
    };

    fetchCities();
  }, [editedData.countryId, isRTL]);

  // Update city name if we have ID but no name (or name is ID)
  useEffect(() => {
    if (cities.length > 0 && editedData.cityId && (!editedData.city || !isNaN(editedData.city))) {
      const city = cities.find(c => c.id === editedData.cityId);
      if (city) {
        const cityName = isRTL ? (city.name_ar || city.name_en) : city.name_en;
        setEditedData(prev => ({ ...prev, city: cityName }));
      }
    }
  }, [cities, editedData.cityId, isRTL]);

  // Update city ID if we have Name but no ID, OR if City IS the ID
  useEffect(() => {
    // Case 1: City is a number (ID)
    if (cities.length > 0 && !editedData.cityId && editedData.city && !isNaN(editedData.city)) {
      const cityId = Number(editedData.city);
      const city = cities.find(c => c.id === cityId);
      if (city) {
        setEditedData(prev => ({ ...prev, cityId: cityId }));
      }
    }
    // Case 2: City is a string (Name) -> Find ID
    else if (cities.length > 0 && !editedData.cityId && editedData.city && isNaN(editedData.city)) {
      const cityNameStr = String(editedData.city).toLowerCase();
      const city = cities.find(c =>
        (c.name_en && String(c.name_en).toLowerCase() === cityNameStr) ||
        (c.name_ar && c.name_ar === editedData.city)
      );
      if (city) {
        setEditedData(prev => ({ ...prev, cityId: city.id }));
      }
    }
  }, [cities, editedData.city, editedData.cityId]);

  // Handle click outside to close country code dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCountryCodeOpen && !event.target.closest('.country-code-container')) {
        setIsCountryCodeOpen(false)
        setCountrySearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isCountryCodeOpen])

  // Country codes with flags
  const countryCodes = [
    { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
    { code: '+971', flag: '🇦🇪', name: 'UAE' },
    { code: '+973', flag: '🇧🇭', name: 'Bahrain' },
    { code: '+974', flag: '🇶🇦', name: 'Qatar' },
    { code: '+965', flag: '🇰🇼', name: 'Kuwait' },
    { code: '+968', flag: '🇴🇲', name: 'Oman' },
    { code: '+962', flag: '🇯🇴', name: 'Jordan' },
    { code: '+961', flag: '🇱🇧', name: 'Lebanon' },
    { code: '+20', flag: '🇪🇬', name: 'Egypt' },
    { code: '+212', flag: '🇲🇦', name: 'Morocco' },
    { code: '+216', flag: '🇹🇳', name: 'Tunisia' },
    { code: '+213', flag: '🇩🇿', name: 'Algeria' },
    { code: '+1', flag: '🇺🇸', name: 'USA' },
    { code: '+44', flag: '🇬🇧', name: 'UK' },
    { code: '+33', flag: '🇫🇷', name: 'France' },
    { code: '+49', flag: '🇩🇪', name: 'Germany' },
    { code: '+91', flag: '🇮🇳', name: 'India' },
    { code: '+86', flag: '🇨🇳', name: 'China' },
    { code: '+81', flag: '🇯🇵', name: 'Japan' },
    { code: '+82', flag: '🇰🇷', name: 'South Korea' },
  ]

  // Update editedData when userData changes (when modal opens)
  useEffect(() => {
    if (!isOpen || !userData) {
      return
    }

    const possibleDateFields = ['dateOfBirth', 'birthDate', 'date_of_birth', 'birth_date', 'dob']
    const foundDateValue = possibleDateFields.map((field) => userData[field]).find(Boolean)

    let formattedDateOfBirth = ''
    if (typeof foundDateValue === 'string') {
      if (foundDateValue.includes('/')) {
        const [day, month, year] = foundDateValue.split('/')
        formattedDateOfBirth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      } else if (foundDateValue.includes('-')) {
        formattedDateOfBirth = foundDateValue
      } else {
        const parsedDate = new Date(foundDateValue)
        if (!Number.isNaN(parsedDate.getTime())) {
          formattedDateOfBirth = parsedDate.toISOString().split('T')[0]
        }
      }
    }

    const formattedUserData = {
      ...userData,
      dateOfBirth: formattedDateOfBirth || userData.dateOfBirth || ''
    }

    setEditedData(formattedUserData)
    setImagePreview(userData.profileImage || null)

    if (userData.phone) {
      const phoneMatch = userData.phone.match(/^\+(\d+)\s/)
      if (phoneMatch) {
        setSelectedCountryCode(`+${phoneMatch[1]}`)
      }
    }

    setOriginalLocation(formattedUserData.location || '')
    setLocationChanged(false)
  }, [isOpen, userData])


  const [isLocationChangeModalOpen, setIsLocationChangeModalOpen] = useState(false)
  const [isLocationSuccessModalOpen, setIsLocationSuccessModalOpen] = useState(false)
  const [oldLocation, setOldLocation] = useState('')
  const [newLocation, setNewLocation] = useState('')
  const [originalLocation, setOriginalLocation] = useState('')
  const [locationChanged, setLocationChanged] = useState(false)

  const handleInputChange = (field, value) => {
    // Check if location is being changed
    if (field === 'location' && value !== editedData.location && value !== '') {
      setOldLocation(editedData.location)
      setNewLocation(value)
      setIsLocationChangeModalOpen(true)
      return // Don't update the state yet, wait for user confirmation
    }

    setEditedData(prev => {
      const newData = {
        ...prev,
        [field]: value
      }

      // Update composite name if name parts change
      if (field === 'firstName' || field === 'lastName') {
        const fName = field === 'firstName' ? value : (newData.firstName || '');
        const lName = field === 'lastName' ? value : (newData.lastName || '');
        newData.name = `${fName} ${lName}`.trim();
      }

      return newData
    })
  }

  const handleLocationChangeConfirm = async () => {
    // Update the location in edited data
    setEditedData(prev => ({
      ...prev,
      location: newLocation
    }))

    // Close location change modal and show success modal
    setIsLocationChangeModalOpen(false)

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000))

    setIsLocationSuccessModalOpen(true)
  }

  const handleLocationChangeKeepCurrent = () => {
    // Reset to original location
    setNewLocation('')
    setOldLocation('')
  }

  const handleLocationSuccessContinue = () => {
    // User confirmed the location change, continue with normal flow
    setLocationChanged(false)
    onClose()
  }

  const handleSave = async () => {
    setIsLoading(true)
    try {
      // Upload image first if selected
      let uploadedImageUrl = null
      if (selectedImage) {
        const myHeaders = new Headers()
        myHeaders.append("Accept", "application/json")
        myHeaders.append("Authorization", `Bearer ${localStorage.getItem('access_token')}`) // Get token from localStorage

        const formdata = new FormData()
        formdata.append("profile_image", selectedImage, selectedImage.name)

        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: formdata,
          redirect: "follow"
        }

        const API_BASE_URL = import.meta.env.VITE_API_URL
        try {
          const response = await fetch(`${API_BASE_URL}/api/v1/user-profile/upload-image`, requestOptions)

          if (!response.ok) {
            throw new Error(`Image upload failed: ${response.status}`)
          }

          const imageResult = await response.json()
          uploadedImageUrl = imageResult.imageUrl || imageResult.url // Adjust based on your API response
        } catch (imageError) {
          throw new Error(t('editProfileModal.errors.uploadFailed'))
        }
      }

      // Include the uploaded image URL in the data to be saved
      const dataToSave = {
        ...editedData,
        profileImage: uploadedImageUrl || imagePreview
      }

      await onSave(dataToSave)

      if (locationChanged) {
        setNewLocation(newLocation)
        setIsLocationSuccessModalOpen(true)
      } else {
        onClose()
      }
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsLoading(false)
    }
  }

  const handleImageChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleCancel = () => {
    setEditedData(userData) // Reset to original data
    setSelectedImage(null)
    setImagePreview(userData.profileImage || null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2000,
          fontFamily: '"Red Hat Display", sans-serif',
          padding: "20px",
          boxSizing: "border-box",
        }}
        onClick={(e) => {
          // Close modal when clicking on backdrop
          if (e.target === e.currentTarget) {
            handleCancel()
          }
        }}
      >
        <div
          className="editprofilemodal-container"
          style={{
            background: "#FFFFFF",
            borderRadius: "20px",
            padding: "50px",
            maxWidth: "900px", // Expanded width
            width: "100%",
            maxHeight: "90vh",
            overflow: "auto",
            boxShadow: "0 20px 25px rgba(0, 0, 0, 0.1)",
            position: "relative",
          }}
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
          dir={i18n.dir()}
        >
          {/* Modal Header */}
          <div
            className="editprofilemodal-header"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "40px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "24px",
              }}
            >
              {/* Profile Image */}
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  background: "#F3F4F6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={() => document.getElementById('profile-image-input').click()}
              >
                <img
                  src={imagePreview || getUserPlaceholderImage(editedData)}
                  alt={t("editProfileModal.avatarAlt", {
                    name: editedData.name || t("editProfileModal.defaultName"),
                  })}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    const fallback = getUserPlaceholderImage(editedData);
                    if (e.target.src !== fallback) {
                      e.target.src = fallback;
                    }
                  }}
                />
                {/* Camera icon overlay */}
                {/* <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    right: "0",
                    width: "32px",
                    height: "32px",
                    background: userTypeColor,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "3px solid #FFFFFF",
                    cursor: "pointer",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4z" />
                    <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                  </svg>
                </div> */}
                {/* Blue verification badge */}
                {/* <div
                  style={{
                    position: "absolute",
                    bottom: "8px",
                    left: "8px",
                    width: "24px",
                    height: "24px",
                    background: "#1976D2",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "3px solid #FFFFFF",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div> */}
              </div>
              {/* Hidden file input */}
              <input
                id="profile-image-input"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />

              {/* User Name */}
              <h2
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "32px",
                  fontWeight: 600,
                  color: "#303E4E",
                  margin: "0",
                }}
              >
                {editedData.name}
              </h2>
            </div>

            {/* Close Button */}
            <button
              onClick={handleCancel}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "12px",
                borderRadius: "12px",
                transition: "background 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#F9FAFB"
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "transparent"
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Form Fields */}
          <div
            className="editprofilemodal-form-fields"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "32px",
              marginBottom: "40px",
            }}
          >
            {getUserType() === 'FIRM' ? (
              <>
                {/* Company Name EN */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.nameEn.label")}
                  </label>
                  <input
                    type="text"
                    value={editedData.nameEn || ''}
                    onChange={(e) => handleInputChange('nameEn', e.target.value)}
                    placeholder={t("editProfileModal.fields.nameEn.placeholder")}
                    style={{
                      width: "100%",
                      height: "56px",
                      padding: "0 20px",
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "14px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      color: "#374151",
                      boxSizing: "border-box",
                      outline: "none",
                      background: "white",
                      transition: "border-color 0.2s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = userTypeColor
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#E5E7EB"
                    }}
                  />
                </div>

                {/* Company Name AR */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.nameAr.label")}
                  </label>
                  <input
                    type="text"
                    value={editedData.nameAr || ''}
                    onChange={(e) => handleInputChange('nameAr', e.target.value)}
                    placeholder={t("editProfileModal.fields.nameAr.placeholder")}
                    style={{
                      width: "100%",
                      height: "56px",
                      padding: "0 20px",
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "14px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      color: "#374151",
                      boxSizing: "border-box",
                      outline: "none",
                      background: "white",
                      transition: "border-color 0.2s ease",
                      textAlign: isRTL ? "right" : "left",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = userTypeColor
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#E5E7EB"
                    }}
                  />
                </div>

                {/* Legal Number */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.legalNumber.label")}
                  </label>
                  <input
                    type="text"
                    value={editedData.legalNumber || ''}
                    onChange={(e) => handleInputChange('legalNumber', e.target.value)}
                    placeholder={t("editProfileModal.fields.legalNumber.placeholder")}
                    style={{
                      width: "100%",
                      height: "56px",
                      padding: "0 20px",
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "14px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      color: "#374151",
                      boxSizing: "border-box",
                      outline: "none",
                      background: "white",
                      transition: "border-color 0.2s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = userTypeColor
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#E5E7EB"
                    }}
                  />
                </div>

                {/* Email (Read-only) */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.email.label")}
                  </label>
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        position: "absolute",
                        left: isRTL ? "auto" : "16px",
                        right: isRTL ? "16px" : "auto",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#9CA3AF",
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      value={editedData.email || ''}
                      readOnly
                      style={{
                        width: "100%",
                        height: "56px",
                        padding: isRTL ? "0 20px 0 52px" : "0 52px 0 20px",
                        border: "1.5px solid #E5E7EB",
                        borderRadius: "14px",
                        fontFamily: '"Red Hat Display", sans-serif',
                        fontSize: "16px",
                        color: "#374151",
                        boxSizing: "border-box",
                        outline: "none",
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                    />
                  </div>
                </div>

                {/* Contact Info */}
                <div style={{ gridColumn: "span 2" }}>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.contactInfo.label")}
                  </label>
                  <textarea
                    value={editedData.contactInfo || editedData.phone || ''}
                    onChange={(e) => handleInputChange('contactInfo', e.target.value)}
                    placeholder={t("editProfileModal.fields.contactInfo.placeholder")}
                    style={{
                      width: "100%",
                      minHeight: "100px",
                      padding: "16px 20px",
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "14px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      color: "#374151",
                      boxSizing: "border-box",
                      outline: "none",
                      background: "white",
                      transition: "border-color 0.2s ease",
                      resize: "vertical",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = userTypeColor
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#E5E7EB"
                    }}
                  />
                </div>

                {/* Address */}
                <div style={{ gridColumn: "span 2" }}>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.address.label")}
                  </label>
                  <input
                    type="text"
                    value={editedData.address || ''}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder={t("editProfileModal.fields.address.placeholder")}
                    style={{
                      width: "100%",
                      height: "56px",
                      padding: "0 20px",
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "14px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      color: "#374151",
                      boxSizing: "border-box",
                      outline: "none",
                      background: "white",
                      transition: "border-color 0.2s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = userTypeColor
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#E5E7EB"
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                {/* First Name */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.firstName.label")}
                  </label>
                  <input
                    type="text"
                    value={editedData.firstName || ''}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder={t("editProfileModal.fields.firstName.placeholder")}
                    style={{
                      width: "100%",
                      height: "56px",
                      padding: "0 20px",
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "14px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      color: "#374151",
                      boxSizing: "border-box",
                      outline: "none",
                      background: "white",
                      transition: "border-color 0.2s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = userTypeColor
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#E5E7EB"
                    }}
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.lastName.label")}
                  </label>
                  <input
                    type="text"
                    value={editedData.lastName || ''}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder={t("editProfileModal.fields.lastName.placeholder")}
                    style={{
                      width: "100%",
                      height: "56px",
                      padding: "0 20px",
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "14px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      color: "#374151",
                      boxSizing: "border-box",
                      outline: "none",
                      background: "white",
                      transition: "border-color 0.2s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = userTypeColor
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#E5E7EB"
                    }}
                  />
                </div>

                {/* Email (Read-only for Personal) */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.email.label")}
                  </label>
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        position: "absolute",
                        left: isRTL ? "auto" : "16px",
                        right: isRTL ? "16px" : "auto",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#9CA3AF",
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      value={editedData.email || ''}
                      readOnly
                      style={{
                        width: "100%",
                        height: "56px",
                        padding: isRTL ? "0 20px 0 52px" : "0 52px 0 20px",
                        border: "1.5px solid #E5E7EB",
                        borderRadius: "14px",
                        fontFamily: '"Red Hat Display", sans-serif',
                        fontSize: "16px",
                        color: "#374151",
                        boxSizing: "border-box",
                        outline: "none",
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.phone.label")}
                  </label>
                  <div style={{ position: "relative", display: "flex" }}>
                    <div
                      className="country-code-container"
                      style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "0 16px",
                        border: "1.5px solid #E5E7EB",
                        borderRadius: isRTL ? "0 14px 14px 0" : "14px 0 0 14px",
                        background: "#F9FAFB",
                        minWidth: "120px",
                        height: "56px",
                        cursor: "pointer",
                      }}
                      onClick={() => setIsCountryCodeOpen(!isCountryCodeOpen)}
                    >
                      <span style={{ fontSize: "20px" }}>
                        {countryCodes.find(c => c.code === selectedCountryCode)?.flag || '🇸🇦'}
                      </span>
                      <span style={{ fontSize: "16px", color: "#374151", fontWeight: 500 }}>
                        {selectedCountryCode}
                      </span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        style={{
                          color: "#9CA3AF",
                          transform: isCountryCodeOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease"
                        }}
                      >
                        <path d="m7 10 5 5 5-5z" />
                      </svg>

                      {isCountryCodeOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "white",
                            border: "1.5px solid #E5E7EB",
                            borderRadius: "8px",
                            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                            zIndex: 1000,
                            maxHeight: "300px",
                            overflowY: "auto",
                            marginTop: "4px",
                          }}
                        >
                          {countryCodes.map((country) => (
                            <div
                              key={country.code}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "12px 16px",
                                cursor: "pointer",
                                transition: "background 0.2s ease",
                              }}
                              onClick={() => {
                                setSelectedCountryCode(country.code)
                                setIsCountryCodeOpen(false)
                                const currentNumber = editedData.phone.replace(/^\+\d+\s/, '')
                                handleInputChange('phone', `${country.code} ${currentNumber}`)
                              }}
                            >
                              <span style={{ fontSize: "20px" }}>{country.flag}</span>
                              <span style={{ fontSize: "14px", color: "#374151" }}>{country.code}</span>
                              <span style={{ fontSize: "12px", color: "#6B7280", marginLeft: "auto" }}>{country.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={editedData.phone.replace(/^\+\d+\s/, "")}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9\-]/g, '');
                        handleInputChange('phone', `${selectedCountryCode} ${value}`);
                      }}
                      placeholder={t("editProfileModal.fields.phone.placeholder")}
                      style={{
                        flex: 1,
                        height: "56px",
                        padding: "0 20px",
                        border: "1.5px solid #E5E7EB",
                        borderLeft: isRTL ? "1.5px solid #E5E7EB" : "none",
                        borderRight: isRTL ? "none" : "1.5px solid #E5E7EB",
                        borderRadius: isRTL ? "14px 0 0 14px" : "0 14px 14px 0",
                        fontSize: "16px",
                        color: "#374151",
                        boxSizing: "border-box",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>

                {/* Date of Birth */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.dateOfBirth.label")}
                  </label>
                  <input
                    type="date"
                    value={editedData.dateOfBirth || ''}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    style={{
                      width: "100%",
                      height: "56px",
                      padding: "0 20px",
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "14px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      color: "#374151",
                      boxSizing: "border-box",
                      outline: "none",
                      background: "white",
                    }}
                  />
                </div>

                {/* Gender */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {t("editProfileModal.fields.gender.label")}
                  </label>
                  <select
                    value={editedData.gender || ''}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    style={{
                      width: "100%",
                      height: "56px",
                      padding: "0 20px",
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "14px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      color: "#374151",
                      boxSizing: "border-box",
                      outline: "none",
                      background: "white",
                      appearance: "none",
                    }}
                  >
                    <option value="" disabled>{t("editProfileModal.fields.gender.placeholder")}</option>
                    <option value="Male">{t("editProfileModal.genderOptions.male")}</option>
                    <option value="Female">{t("editProfileModal.genderOptions.female")}</option>
                    <option value="Prefer not to say">{t("editProfileModal.genderOptions.preferNot")}</option>
                  </select>
                </div>
              </>
            )}

            {/* Country (Shared) */}
            <div>
              <label
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#6B7280",
                  display: "block",
                  marginBottom: "12px",
                }}
              >
                {t("editProfileModal.fields.country.label")}
              </label>
              <Select
                options={countryOptions}
                value={countryOptions.find(opt => opt.value == editedData.countryId) || null}
                onChange={(option) => {
                  handleInputChange('countryId', option ? option.value : null);
                  handleInputChange('country', option ? option.label : '');
                  handleInputChange('cityId', null);
                  handleInputChange('city', '');
                }}
                placeholder={t("editProfileModal.fields.country.placeholder")}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    height: '56px',
                    borderRadius: '14px',
                    border: state.isFocused ? `1.5px solid ${userTypeColor}` : '1.5px solid #E5E7EB',
                    boxShadow: 'none',
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
              />
            </div>

            {/* City (Shared) */}
            <div>
              <label
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#6B7280",
                  display: "block",
                  marginBottom: "12px",
                }}
              >
                {t("editProfileModal.fields.city.label")}
              </label>
              <Select
                options={cityOptions}
                value={cityOptions.find(opt => opt.value == editedData.cityId) || null}
                onChange={(option) => {
                  handleInputChange('cityId', option ? option.value : null);
                  handleInputChange('city', option ? option.label : '');
                }}
                placeholder={t("editProfileModal.fields.city.placeholder")}
                isDisabled={!editedData.countryId}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    height: '56px',
                    borderRadius: '14px',
                    border: state.isFocused ? `1.5px solid ${userTypeColor}` : '1.5px solid #E5E7EB',
                    boxShadow: 'none',
                    backgroundColor: !editedData.countryId ? '#F3F4FB' : 'white',
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div
            className="editprofilemodal-actions"
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
            }}
          >
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="editprofilemodal-save-btn"
              style={{
                width: "160px",
                height: "56px",
                background: isLoading ? "#9CA3AF" : userTypeColor,
                color: "#FFFFFF",
                border: "none",
                borderRadius: "14px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "18px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                boxShadow: isLoading ? "none" : `0 4px 12px ${userTypeColor}40`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.background = hoverColor
                  e.target.style.transform = "translateY(-2px)"
                  e.target.style.boxShadow = `0 6px 20px ${userTypeColor}60`
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.background = userTypeColor
                  e.target.style.transform = "translateY(0)"
                  e.target.style.boxShadow = `0 4px 12px ${userTypeColor}40`
                }
              }}
            >
              {isLoading ? (
                <>
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      border: "2px solid #FFFFFF",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  {t("editProfileModal.buttons.saving")}
                </>
              ) : (
                t("editProfileModal.buttons.save")
              )}
            </button>
            <button
              onClick={handleCancel}
              disabled={isLoading}
              className="editprofilemodal-cancel-btn"
              style={{
                width: "160px",
                height: "56px",
                background: "#FFFFFF",
                color: isLoading ? "#9CA3AF" : "#6B7280",
                border: "2px solid #E5E7EB",
                borderRadius: "14px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "18px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                opacity: isLoading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.borderColor = "#D1D5DB"
                  e.target.style.color = "#374151"
                  e.target.style.transform = "translateY(-2px)"
                  e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)"
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.borderColor = "#E5E7EB"
                  e.target.style.color = "#6B7280"
                  e.target.style.transform = "translateY(0)"
                  e.target.style.boxShadow = "none"
                }
              }}
            >
              {t("editProfileModal.buttons.cancel")}
            </button>
          </div>
        </div>
      </div>

      {/* Location Change Modal */}
      <LocationChangeModal
        isOpen={isLocationChangeModalOpen}
        onClose={() => setIsLocationChangeModalOpen(false)}
        oldLocation={oldLocation}
        newLocation={newLocation}
        onConfirm={handleLocationChangeConfirm}
        onKeepCurrent={handleLocationChangeKeepCurrent}
      />

      {/* Location Success Modal */}
      <LocationSuccessModal
        isOpen={isLocationSuccessModalOpen}
        onClose={() => setIsLocationSuccessModalOpen(false)}
        newLocation={newLocation}
        onContinue={handleLocationSuccessContinue}
      />
    </>
  )
}

export default EditProfileModal